import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Receipt, Banknote, Plus, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { useAuth } from "@/contexts/AuthContext";
import { EstimateAPI, InvoiceAPI, extractError } from "@/lib/api";
import { PaymentDialog } from "@/components/PaymentDialog";
import { GatewayCheckoutButton } from "@/components/GatewayCheckoutButton";

const inr = (m) => `₹${(Math.round((m || 0) / 100)).toLocaleString("en-IN")}`;

/**
 * BillingTab — embedded on the Job Detail page.
 * Surfaces the latest linked estimate + invoice + a quick "create from job" CTA.
 * Lets accountants record payments without leaving the job.
 */
export function BillingTab({ jobId }) {
    const { permissions } = useAuth();
    const [estimate, setEstimate] = useState(null);
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        setLoading(true);
        try {
            // Latest estimate linked to this job
            const e = await EstimateAPI.list({ page_size: 50 }).catch(() => null);
            const myEst = (e?.items || []).find((x) => x.job_card_id === jobId) || null;
            setEstimate(myEst);
            // Latest invoice linked to this job
            const inv = await InvoiceAPI.list({ page_size: 50 }).catch(() => null);
            const myInv = (inv?.items || []).find((x) => x.job_card_id === jobId) || null;
            setInvoice(myInv);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [jobId]);

    const canEst = permissions.includes("estimate:create");
    const canInv = permissions.includes("invoice:create");

    return (
        <div className="space-y-4" data-testid="billing-tab-panel">
            <div className="grid gap-4 md:grid-cols-2">
                {/* Estimate card */}
                <Card data-testid="billing-estimate-card">
                    <CardContent className="p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="font-display text-sm font-bold uppercase tracking-tight text-zinc-500">Estimate</h3>
                            {!estimate && canEst && (
                                <Link to={`/estimates/new?job_card_id=${jobId}`}>
                                    <Button size="sm" data-testid="billing-create-estimate"
                                            className="bg-brand-red text-white hover:bg-brand-redDeep">
                                        <Plus className="mr-1 h-3 w-3" />Create estimate from job
                                    </Button>
                                </Link>
                            )}
                        </div>
                        {loading ? (
                            <p className="text-xs text-zinc-500">Loading…</p>
                        ) : estimate ? (
                            <>
                                <div className="text-sm">
                                    <div className="font-mono" data-testid="billing-estimate-number">{estimate.estimate_number}</div>
                                    <div className="text-xs text-zinc-500">Status <span className="font-mono">{estimate.status}</span></div>
                                </div>
                                <div className="text-sm font-mono">Total {inr(estimate.total_minor)}</div>
                                <Link to={`/estimates/${estimate.id}`}>
                                    <Button variant="outline" size="sm" data-testid="billing-open-estimate"><ArrowRight className="mr-1 h-3 w-3" />Open</Button>
                                </Link>
                            </>
                        ) : (
                            <p className="text-xs text-zinc-500">No estimate linked to this job yet.</p>
                        )}
                    </CardContent>
                </Card>

                {/* Invoice card */}
                <Card data-testid="billing-invoice-card">
                    <CardContent className="p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="font-display text-sm font-bold uppercase tracking-tight text-zinc-500">Invoice</h3>
                            {!invoice && canInv && (
                                <Link to={`/invoices/new?job_card_id=${jobId}`}>
                                    <Button size="sm" variant="outline" data-testid="billing-create-invoice"><Plus className="mr-1 h-3 w-3" />Direct invoice</Button>
                                </Link>
                            )}
                        </div>
                        {loading ? (
                            <p className="text-xs text-zinc-500">Loading…</p>
                        ) : invoice ? (
                            <>
                                <div className="text-sm">
                                    <div className="font-mono" data-testid="billing-invoice-number">{invoice.invoice_number}</div>
                                    <div className="text-xs text-zinc-500">Status <span className="font-mono">{invoice.status}</span></div>
                                </div>
                                <div className="text-sm font-mono">Total {inr(invoice.total_minor)}</div>
                                <div className="text-sm font-mono">Due <b className="text-brand-redDeep dark:text-brand-red">{inr(invoice.amount_due_minor)}</b></div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Link to={`/invoices/${invoice.id}`}>
                                        <Button variant="outline" size="sm" data-testid="billing-open-invoice"><ArrowRight className="mr-1 h-3 w-3" />Open</Button>
                                    </Link>
                                    {["issued", "partially_paid", "overdue"].includes(invoice.status) && (
                                        <PaymentDialog invoice={invoice} onPaid={refresh}
                                                       triggerLabel="Record"
                                                       testid="billing-record-payment-btn" />
                                    )}
                                    {["issued", "partially_paid", "overdue"].includes(invoice.status) && (
                                        <GatewayCheckoutButton invoice={invoice} onCaptured={refresh} />
                                    )}
                                </div>
                            </>
                        ) : (
                            <p className="text-xs text-zinc-500">No invoice yet. Convert the estimate or create a walk-in invoice.</p>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
